import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { LOT_TYPE_ENUM, LotTypeEnumId } from '@classictechsolutions/hubapi-transpiled-enums';
import { ILotAdd } from '../lot-migrator.models';
import { buildMigratingHeader } from '../lot-migrator.util';

@Component({
    selector: 'cb-lot-migrator-target-config',
    standalone: true,
    imports: [MatButtonModule],
    templateUrl: './lot-migrator-target-config.component.html',
    styleUrls: ['./lot-migrator-target-config.component.scss']
})
export class LotMigratorTargetConfigComponent implements OnChanges, OnInit {
    @Input() public migrationLotType: LotTypeEnumId;
    @Input() public rebuildTrigger = 0;
    @Output() public lotAddMapChange = new EventEmitter<ILotAdd[]>();
    @Output() public countsChange = new EventEmitter<{ lots: number; units: number }>();

    public readonly LOT_TYPE_ENUM = LOT_TYPE_ENUM;
    public numberOfLotsToAdd = 1;
    public numberOfUnitsToAdd = 2;
    public lotAddMap: ILotAdd[] = [];

    public ngOnInit(): void {
        this.resetLotAddMap();
        this.emitChanges();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            this.migrationLotType != null &&
            ((changes['migrationLotType'] && !changes['migrationLotType'].firstChange) ||
                (changes['rebuildTrigger'] && !changes['rebuildTrigger'].firstChange))
        ) {
            this.resetLotAddMap();
            this.emitChanges();
        }
    }

    public getMigratingToHeader(): string {
        const numberOfLots = this.lotAddMap.length;
        const unitsPerBlock = this.lotAddMap[0]?.units.length;

        return buildMigratingHeader({
            prefix: 'To',
            lotType: this.migrationLotType,
            numberOfLots,
            unitsPerBlock
        });
    }

    public handleNumberOfLots(lotsToAddOrRemove: number): void {
        this.numberOfLotsToAdd = this.numberOfLotsToAdd + lotsToAddOrRemove;
        this.resetLotAddMap();
        this.emitChanges();
    }

    public handleNumberOfUnits(unitsToAddOrRemove: number): void {
        const updatedUnits = this.numberOfUnitsToAdd + unitsToAddOrRemove;
        if (updatedUnits < 2) {
            return;
        }
        this.numberOfUnitsToAdd = updatedUnits;
        this.resetLotAddMap();
        this.emitChanges();
    }

    private emitChanges(): void {
        this.lotAddMapChange.emit(this.lotAddMap);
        this.countsChange.emit({
            lots: this.numberOfLotsToAdd,
            units: this.numberOfUnitsToAdd
        });
    }

    private resetLotAddMap(): void {
        const lots: ILotAdd[] = [];

        for (let i = 1; i <= this.numberOfLotsToAdd; i++) {
            lots.push(this.buildLot(i));
        }

        this.lotAddMap = lots;
    }

    private buildLot(index: number): ILotAdd {
        const name = this.buildLotName(index);
        const lot: ILotAdd = {
            id: index,
            name,
            units: [],
            documents: []
        };

        if (this.migrationLotType === LotTypeEnumId.TerracedBlock) {
            lot.units = this.buildUnits(index, name);
        }

        return lot;
    }

    private buildLotName(index: number): string {
        if (this.migrationLotType === LotTypeEnumId.TerracedBlock) {
            return 'Block ' + index;
        }

        return 'Lot ' + index;
    }

    private buildUnits(index: number, lotName: string): ILotAdd[] {
        const units: ILotAdd[] = [];

        for (let i = 1; i <= this.numberOfUnitsToAdd; i++) {
            units.push({
                id: index * i + i,
                name: lotName + ' Unit ' + i,
                units: [],
                documents: []
            });
        }

        return units;
    }
}
